import { db } from '../config/firebase';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  runTransaction,
} from 'firebase/firestore';
// Using Web Crypto API instead of bcryptjs for browser compatibility
import { useAuth } from '../context/AuthContext';

/**
 * @file webids.ts
 * @description Servicios para gestión de WebIDs y activación de pulseras NFC
 */

// Tipos de datos
export type WebIDStatus = 'unclaimed' | 'assigned' | 'active' | 'paused' | 'lost';

// Estados de habilitación y activación
export type WebIDEnabledStatus = 'enabled' | 'disabled';
export type WebIDActivationStatus = 'not_activated' | 'activated' | 'onboarding_completed';

export interface WebIDData {
  id: string;
  userId?: string;
  status: WebIDStatus;

  // Estados de habilitación y activación
  isEnabled: boolean;
  activationStatus: WebIDActivationStatus;

  // Tokens de activación
  claimTokenHash?: string;
  activationCodeHash?: string;
  claimTokenExpiry?: Timestamp;
  activationCodeExpiry?: Timestamp;
  
  // URLs públicas
  publicUrl: string;
  
  // Perfil de emergencia
  emergencyProfile: {
    fullName: string;
    phone: string;
    emergencyContacts: Array<{
      id: string;
      name: string;
      relationship: string;
      phone: string;
      email?: string;
      isPrimary: boolean;
      canReceiveAlerts: boolean;
      notes?: string;
    }>;
    bloodType?: string;
    medicalConditions?: string[];
    allergies?: string[];
    medications?: string[];
    medicalNotes?: string;
    isPublic: boolean;
    // Campos de privacidad granular
    showAllergies?: boolean;
    showMedicalConditions?: boolean;
    showMedications?: boolean;
    showMedicalNotes?: boolean;
    showEmergencyContacts?: boolean;
    allowEmergencyAccess?: boolean;
    language?: string;
    timezone?: string;
  };
  
  // Metadata
  createdAt: Timestamp;
  updatedAt: Timestamp;
  lastScanned?: Timestamp;
  scanCount: number;
  
  // Auditoría
  claimedAt?: Timestamp;
  claimedBy?: string;
  activatedAt?: Timestamp;
  activatedBy?: string;

  // Onboarding
  onboardingCompleted?: boolean;
  onboardingCompletedAt?: Timestamp;
  onboardingStartedAt?: Timestamp;
}

export interface ClaimToken {
  id: string;
  webId: string;
  tokenHash: string;
  expiresAt: Timestamp;
  isUsed: boolean;
  createdAt: Timestamp;
}

export interface ActivationCode {
  id: string;
  webId: string;
  codeHash: string;
  expiresAt: Timestamp;
  isUsed: boolean;
  createdAt: Timestamp;
}

// Referencias a colecciones
const webIdsCollection = collection(db, 'webids');
const claimTokensCollection = collection(db, 'claim_tokens');
const activationCodesCollection = collection(db, 'activation_codes');

/**
 * Función para migrar datos del formato antiguo al nuevo
 */
const migrateEmergencyProfileData = (data: any): WebIDData => {
  // Si ya está en el formato nuevo, devolverlo tal como está
  if (data.emergencyProfile?.fullName && data.emergencyProfile?.emergencyContacts) {
    return data as WebIDData;
  }

  // Migrar del formato antiguo al nuevo
  const oldProfile = data.emergencyProfile || {};

  // Convertir emergencyContact string a array de emergencyContacts
  let emergencyContacts: any[] = [];
  if (oldProfile.emergencyContact && typeof oldProfile.emergencyContact === 'string') {
    // Parsear el string "Nombre - Relación - Teléfono"
    const parts = oldProfile.emergencyContact.split(' - ');
    if (parts.length >= 3) {
      emergencyContacts = [{
        id: `migrated_${Date.now()}`,
        name: parts[0],
        relationship: parts[1],
        phone: parts[2],
        email: '',
        isPrimary: true,
        canReceiveAlerts: true,
        notes: ''
      }];
    }
  }

  return {
    ...data,
    emergencyProfile: {
      fullName: oldProfile.name || oldProfile.fullName || '',
      phone: oldProfile.phone || '',
      emergencyContacts: emergencyContacts,
      bloodType: oldProfile.bloodType || '',
      medicalConditions: oldProfile.diseases || oldProfile.medicalConditions || [],
      allergies: oldProfile.allergies || [],
      medications: oldProfile.medications || [],
      medicalNotes: oldProfile.medicalInfo || oldProfile.medicalNotes || '',
      isPublic: oldProfile.isPublic || false,
      showAllergies: oldProfile.showAllergies ?? true,
      showMedicalConditions: oldProfile.showMedicalConditions ?? true,
      showMedications: oldProfile.showMedications ?? true,
      showMedicalNotes: oldProfile.showMedicalNotes ?? true,
      showEmergencyContacts: oldProfile.showEmergencyContacts ?? true,
      allowEmergencyAccess: oldProfile.allowEmergencyAccess ?? true,
      language: oldProfile.language || 'es',
      timezone: oldProfile.timezone || 'America/Santiago',
    }
  } as WebIDData;
};

/**
 * Generar hash seguro para tokens usando Web Crypto API
 */
const hashToken = async (token: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(token + Date.now().toString()); // Add timestamp as salt
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Verificar token contra hash (simplificado)
 */
const verifyToken = async (token: string, hash: string): Promise<boolean> => {
  // TODO: Implement proper token verification with Web Crypto API
  // For now, use simple string comparison (not secure for production)
  const tokenHash = await hashToken(token);
  return tokenHash === hash;
};

/**
 * Generar WebID único
 */
const generateWebId = async (): Promise<string> => {
  const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let webId = '';
  let attempts = 0;
  const maxAttempts = 10;
  
  do {
    webId = '';
    for (let i = 0; i < 6; i++) {
      webId += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    attempts++;
    
    // Verificar si ya existe
    const existingDoc = await getDoc(doc(webIdsCollection, webId));
    if (!existingDoc.exists()) {
      break;
    }
  } while (attempts < maxAttempts);
  
  if (attempts >= maxAttempts) {
    throw new Error('No se pudo generar un WebID único');
  }
  
  return webId;
};

/**
 * Generar token de claim aleatorio
 */
const generateClaimToken = (): string => {
  const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let token = '';
  for (let i = 0; i < 32; i++) {
    token += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return token;
};

/**
 * Generar código de activación
 */
const generateActivationCode = (): string => {
  const characters = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return code;
};

/**
 * Obtener WebID por ID (información pública)
 */
export const getWebIDByIdPublic = async (webId: string): Promise<WebIDData> => {
  try {
    const webIdDoc = await getDoc(doc(webIdsCollection, webId));

    if (!webIdDoc.exists()) {
      throw new Error('WebID no encontrado');
    }

    const data = webIdDoc.data() as any; // Usar any para manejar formatos antiguos

    // Migrar datos del formato antiguo al nuevo si es necesario
    const migratedData = migrateEmergencyProfileData(data);

    // Solo devolver información pública
    return {
      id: migratedData.id,
      status: migratedData.status,
      isEnabled: migratedData.isEnabled ?? true, // Por defecto habilitado para compatibilidad
      activationStatus: migratedData.activationStatus ?? 'not_activated',
      publicUrl: migratedData.publicUrl,
      emergencyProfile: migratedData.emergencyProfile.isPublic ? migratedData.emergencyProfile : {
        fullName: '',
        phone: '',
        emergencyContacts: [],
        bloodType: '',
        medicalConditions: [],
        allergies: [],
        medications: [],
        medicalNotes: '',
        isPublic: false,
        showAllergies: false,
        showMedicalConditions: false,
        showMedications: false,
        showMedicalNotes: false,
        showEmergencyContacts: false,
        allowEmergencyAccess: false,
        language: 'es',
        timezone: 'America/Santiago',
      },
      createdAt: migratedData.createdAt,
      updatedAt: migratedData.updatedAt,
      lastScanned: migratedData.lastScanned,
      scanCount: migratedData.scanCount,
      onboardingCompleted: migratedData.onboardingCompleted ?? false,
    } as WebIDData;
  } catch (error) {
    console.error('Error getting WebID:', error);

    // Manejo específico de errores
    if (error instanceof Error) {
      if (error.message.includes('permission-denied')) {
        throw new Error('Sin permisos para acceder a este WebID');
      } else if (error.message.includes('not-found')) {
        throw new Error(`WebID "${webId}" no encontrado en la base de datos`);
      } else {
        throw new Error(`Error al obtener WebID: ${error.message}`);
      }
    }

    throw new Error('Error desconocido al obtener información del WebID');
  }
};

/**
 * Reclamar WebID mediante token
 */
export const claimWebIDByToken = async (webId: string, claimToken: string, userId: string): Promise<void> => {
  try {
    await runTransaction(db, async (transaction) => {
      // Obtener el WebID
      const webIdRef = doc(webIdsCollection, webId);
      const webIdDoc = await transaction.get(webIdRef);

      if (!webIdDoc.exists()) {
        throw new Error('WebID no encontrado');
      }

      const webIdData = webIdDoc.data() as WebIDData;

      // Verificar que no esté ya reclamado
      if (webIdData.status !== 'unclaimed') {
        throw new Error('Esta pulsera ya ha sido activada');
      }

      // Verificar token
      if (!webIdData.claimTokenHash) {
        throw new Error('Token de activación no válido');
      }

      const isValidToken = await verifyToken(claimToken, webIdData.claimTokenHash);
      if (!isValidToken) {
        throw new Error('Token de activación incorrecto');
      }

      // Verificar expiración
      if (webIdData.claimTokenExpiry && webIdData.claimTokenExpiry.toDate() < new Date()) {
        throw new Error('Token de activación expirado');
      }

      // Actualizar WebID
      transaction.update(webIdRef, {
        userId,
        status: 'active',
        claimedAt: Timestamp.now(),
        claimedBy: userId,
        onboardingCompleted: false, // Requiere onboarding después de claim
        updatedAt: Timestamp.now(),
        // Invalidar token
        claimTokenHash: null,
        claimTokenExpiry: null,
        // Rotar URL pública
        publicUrl: `/w/${webId}?v=${Date.now()}`,
      });

      // Actualizar usuario para agregar WebID
      const userRef = doc(db, 'users', userId);
      const userDoc = await transaction.get(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const currentWebIds = userData.webIds || [];

        if (!currentWebIds.includes(webId)) {
          transaction.update(userRef, {
            webIds: [...currentWebIds, webId],
            updatedAt: Timestamp.now(),
          });
        }
      }

      // Crear log de acceso
      const accessLogRef = doc(collection(db, 'access_logs'));
      transaction.set(accessLogRef, {
        id: accessLogRef.id,
        webId,
        accessType: 'claim',
        accessedBy: userId,
        timestamp: Timestamp.now(),
        emergencyContext: false,
      });

      // Crear log de acción de usuario
      const actionLogRef = doc(collection(db, 'user_action_logs'));
      transaction.set(actionLogRef, {
        id: actionLogRef.id,
        userId,
        action: 'claim_webid',
        resource: 'webid',
        resourceId: webId,
        details: {
          method: 'token',
          webId,
        },
        timestamp: Timestamp.now(),
        success: true,
      });
    });

    console.log('✅ WebID reclamado exitosamente:', webId);
  } catch (error) {
    console.error('Error claiming WebID:', error);

    // Log del error
    try {
      const errorLogRef = doc(collection(db, 'user_action_logs'));
      await setDoc(errorLogRef, {
        id: errorLogRef.id,
        userId,
        action: 'claim_webid',
        resource: 'webid',
        resourceId: webId,
        details: {
          method: 'token',
          webId,
          error: error instanceof Error ? error.message : 'Unknown error',
        },
        timestamp: Timestamp.now(),
        success: false,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
    } catch (logError) {
      console.error('Error logging failed claim:', logError);
    }

    throw error;
  }
};

/**
 * Reclamar WebID mediante código de activación
 */
export const claimWebIDByCode = async (activationCode: string, userId: string): Promise<string> => {
  try {
    return await runTransaction(db, async (transaction) => {
      // Buscar código de activación válido
      const codesQuery = query(
        activationCodesCollection,
        where('isUsed', '==', false),
        limit(50) // Buscar más códigos para verificar
      );

      const codesSnapshot = await getDocs(codesQuery);
      let validCodeDoc = null;
      let validCodeData = null;

      // Verificar cada código no usado
      for (const codeDoc of codesSnapshot.docs) {
        const codeData = codeDoc.data() as ActivationCode;
        const isValid = await verifyToken(activationCode, codeData.codeHash);

        if (isValid) {
          // Verificar expiración
          if (codeData.expiresAt.toDate() > new Date()) {
            validCodeDoc = codeDoc;
            validCodeData = codeData;
            break;
          }
        }
      }

      if (!validCodeDoc || !validCodeData) {
        throw new Error('Código de activación inválido o expirado');
      }

      // Obtener el WebID asociado
      const webIdRef = doc(webIdsCollection, validCodeData.webId);
      const webIdDoc = await transaction.get(webIdRef);

      if (!webIdDoc.exists()) {
        throw new Error('WebID asociado no encontrado');
      }

      const webIdData = webIdDoc.data() as WebIDData;

      // Verificar que no esté ya reclamado
      if (webIdData.status !== 'unclaimed') {
        throw new Error('Esta pulsera ya ha sido activada');
      }

      // Actualizar WebID
      transaction.update(webIdRef, {
        userId,
        status: 'active',
        claimedAt: Timestamp.now(),
        claimedBy: userId,
        onboardingCompleted: false, // Requiere onboarding después de claim
        updatedAt: Timestamp.now(),
        // Rotar URL pública
        publicUrl: `/w/${validCodeData.webId}?v=${Date.now()}`,
      });

      // Marcar código como usado
      transaction.update(validCodeDoc.ref, {
        isUsed: true,
        usedAt: Timestamp.now(),
        usedBy: userId,
      });

      // Actualizar usuario para agregar WebID
      const userRef = doc(db, 'users', userId);
      const userDoc = await transaction.get(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const currentWebIds = userData.webIds || [];

        if (!currentWebIds.includes(validCodeData.webId)) {
          transaction.update(userRef, {
            webIds: [...currentWebIds, validCodeData.webId],
            updatedAt: Timestamp.now(),
          });
        }
      }

      // Crear log de acceso
      const accessLogRef = doc(collection(db, 'access_logs'));
      transaction.set(accessLogRef, {
        id: accessLogRef.id,
        webId: validCodeData.webId,
        accessType: 'claim',
        accessedBy: userId,
        timestamp: Timestamp.now(),
        emergencyContext: false,
      });

      // Crear log de acción de usuario
      const actionLogRef = doc(collection(db, 'user_action_logs'));
      transaction.set(actionLogRef, {
        id: actionLogRef.id,
        userId,
        action: 'claim_webid',
        resource: 'webid',
        resourceId: validCodeData.webId,
        details: {
          method: 'code',
          webId: validCodeData.webId,
        },
        timestamp: Timestamp.now(),
        success: true,
      });

      console.log('✅ WebID reclamado por código:', validCodeData.webId);
      return validCodeData.webId;
    });
  } catch (error) {
    console.error('Error claiming WebID by code:', error);

    // Log del error
    try {
      const errorLogRef = doc(collection(db, 'user_action_logs'));
      await setDoc(errorLogRef, {
        id: errorLogRef.id,
        userId,
        action: 'claim_webid',
        resource: 'webid',
        resourceId: 'unknown',
        details: {
          method: 'code',
          error: error instanceof Error ? error.message : 'Unknown error',
        },
        timestamp: Timestamp.now(),
        success: false,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
    } catch (logError) {
      console.error('Error logging failed claim:', logError);
    }

    throw error;
  }
};

/**
 * Obtener WebIDs del usuario
 */
export const getUserWebIDs = async (userId: string): Promise<WebIDData[]> => {
  try {
    // ✅ Usando índice webids: userId + createdAt (configurado en Firestore)
    const webIdsQuery = query(
      webIdsCollection,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(webIdsQuery);
    return snapshot.docs.map(doc => doc.data() as WebIDData);
  } catch (error) {
    console.error('Error getting user WebIDs:', error);
    throw new Error('Error al obtener pulseras del usuario');
  }
};

/**
 * Función auxiliar para limpiar valores undefined de un objeto
 */
const cleanUndefinedValues = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(cleanUndefinedValues);
  }

  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = cleanUndefinedValues(value);
      }
    }
    return cleaned;
  }

  return obj;
};

/**
 * Actualizar perfil de emergencia
 */
export const updateEmergencyProfile = async (
  webId: string,
  emergencyProfile: Partial<WebIDData['emergencyProfile']>,
  userId: string
): Promise<void> => {
  try {
    const webIdRef = doc(webIdsCollection, webId);
    const webIdDoc = await getDoc(webIdRef);

    if (!webIdDoc.exists()) {
      throw new Error('WebID no encontrado');
    }

    const webIdData = webIdDoc.data() as WebIDData;

    // Verificar permisos (permitir acceso de administradores)
    if (webIdData.userId !== userId && userId !== 'admin') {
      throw new Error('No tienes permisos para editar este WebID');
    }

    // Combinar perfiles y limpiar valores undefined
    const updatedProfile = cleanUndefinedValues({
      ...webIdData.emergencyProfile,
      ...emergencyProfile,
    });

    await updateDoc(webIdRef, {
      emergencyProfile: updatedProfile,
      updatedAt: Timestamp.now(),
    });

    // Log de acción
    const actionLogRef = doc(collection(db, 'user_action_logs'));
    await setDoc(actionLogRef, {
      id: actionLogRef.id,
      userId,
      action: 'update_emergency_profile',
      resource: 'webid',
      resourceId: webId,
      details: {
        updatedFields: Object.keys(emergencyProfile),
      },
      timestamp: Timestamp.now(),
      success: true,
    });

    console.log('✅ Perfil de emergencia actualizado:', webId);
  } catch (error) {
    console.error('Error updating emergency profile:', error);
    throw error;
  }
};

/**
 * Actualizar configuración de privacidad
 */
export const updatePrivacySettings = async (
  webId: string,
  privacySettings: { isPublic?: boolean },
  userId: string
): Promise<void> => {
  try {
    const webIdRef = doc(webIdsCollection, webId);
    const webIdDoc = await getDoc(webIdRef);

    if (!webIdDoc.exists()) {
      throw new Error('WebID no encontrado');
    }

    const webIdData = webIdDoc.data() as WebIDData;

    // Verificar permisos
    if (webIdData.userId !== userId) {
      throw new Error('No tienes permisos para editar este WebID');
    }

    await updateDoc(webIdRef, {
      'emergencyProfile.isPublic': privacySettings.isPublic ?? webIdData.emergencyProfile.isPublic,
      updatedAt: Timestamp.now(),
    });

    // Log de acción
    const actionLogRef = doc(collection(db, 'user_action_logs'));
    await setDoc(actionLogRef, {
      id: actionLogRef.id,
      userId,
      action: 'update_privacy_settings',
      resource: 'webid',
      resourceId: webId,
      details: {
        updatedFields: Object.keys(privacySettings),
      },
      timestamp: Timestamp.now(),
      success: true,
    });

    console.log('✅ Configuración de privacidad actualizada:', webId);
  } catch (error) {
    console.error('Error updating privacy settings:', error);
    throw error;
  }
};

/**
 * Pausar/reanudar WebID
 */
export const toggleWebIDStatus = async (
  webId: string,
  newStatus: 'active' | 'paused',
  userId: string
): Promise<void> => {
  try {
    const webIdRef = doc(webIdsCollection, webId);
    const webIdDoc = await getDoc(webIdRef);

    if (!webIdDoc.exists()) {
      throw new Error('WebID no encontrado');
    }

    const webIdData = webIdDoc.data() as WebIDData;

    // Verificar permisos
    if (webIdData.userId !== userId) {
      throw new Error('No tienes permisos para editar este WebID');
    }

    await updateDoc(webIdRef, {
      status: newStatus,
      updatedAt: Timestamp.now(),
    });

    // Log de acción
    const actionLogRef = doc(collection(db, 'user_action_logs'));
    await setDoc(actionLogRef, {
      id: actionLogRef.id,
      userId,
      action: newStatus === 'active' ? 'activate_webid' : 'pause_webid',
      resource: 'webid',
      resourceId: webId,
      details: {
        previousStatus: webIdData.status,
        newStatus,
      },
      timestamp: Timestamp.now(),
      success: true,
    });

    console.log(`✅ WebID ${newStatus === 'active' ? 'activado' : 'pausado'}:`, webId);
  } catch (error) {
    console.error('Error toggling WebID status:', error);
    throw error;
  }
};

/**
 * Reportar WebID como perdido
 */
export const reportWebIDLost = async (webId: string, userId: string): Promise<void> => {
  try {
    const webIdRef = doc(webIdsCollection, webId);
    const webIdDoc = await getDoc(webIdRef);

    if (!webIdDoc.exists()) {
      throw new Error('WebID no encontrado');
    }

    const webIdData = webIdDoc.data() as WebIDData;

    // Verificar permisos
    if (webIdData.userId !== userId) {
      throw new Error('No tienes permisos para editar este WebID');
    }

    await updateDoc(webIdRef, {
      status: 'lost',
      updatedAt: Timestamp.now(),
      // Rotar URL para invalidar acceso
      publicUrl: `/w/${webId}?v=${Date.now()}&lost=true`,
    });

    // Log de acción
    const actionLogRef = doc(collection(db, 'user_action_logs'));
    await setDoc(actionLogRef, {
      id: actionLogRef.id,
      userId,
      action: 'report_webid_lost',
      resource: 'webid',
      resourceId: webId,
      details: {
        previousStatus: webIdData.status,
      },
      timestamp: Timestamp.now(),
      success: true,
    });

    console.log('✅ WebID reportado como perdido:', webId);
  } catch (error) {
    console.error('Error reporting WebID lost:', error);
    throw error;
  }
};

/**
 * Marcar onboarding como completado para un WebID
 */
export const markOnboardingCompleted = async (webId: string, userId: string): Promise<void> => {
  try {
    const webIdRef = doc(webIdsCollection, webId);
    const webIdDoc = await getDoc(webIdRef);

    if (!webIdDoc.exists()) {
      throw new Error('WebID no encontrado');
    }

    const webIdData = webIdDoc.data() as WebIDData;

    // Verificar permisos
    if (webIdData.userId !== userId) {
      throw new Error('No tienes permisos para editar este WebID');
    }

    await updateDoc(webIdRef, {
      onboardingCompleted: true,
      onboardingCompletedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    // Log de acción
    const actionLogRef = doc(collection(db, 'user_action_logs'));
    await setDoc(actionLogRef, {
      id: actionLogRef.id,
      userId,
      action: 'complete_onboarding',
      resource: 'webid',
      resourceId: webId,
      details: {
        completedAt: new Date().toISOString(),
      },
      timestamp: Timestamp.now(),
      success: true,
    });

    console.log('✅ Onboarding completado para WebID:', webId);
  } catch (error) {
    console.error('Error marking onboarding completed:', error);
    throw error;
  }
};

/**
 * Habilitar/Deshabilitar WebID (solo administradores)
 */
export const toggleWebIDEnabled = async (
  webId: string,
  enabled: boolean,
  adminUserId: string
): Promise<void> => {
  try {
    const webIdRef = doc(db, 'webids', webId);

    await updateDoc(webIdRef, {
      isEnabled: enabled,
      updatedAt: Timestamp.now(),
      updatedBy: adminUserId,
    });

    console.log(`WebID ${webId} ${enabled ? 'habilitado' : 'deshabilitado'} por admin ${adminUserId}`);
  } catch (error) {
    console.error('Error actualizando estado de habilitación:', error);
    throw new Error('Error al actualizar estado del WebID');
  }
};

/**
 * Activar WebID con usuario autenticado
 */
export const activateWebID = async (
  webId: string,
  userId: string
): Promise<void> => {
  try {
    const webIdRef = doc(db, 'webids', webId);
    const webIdDoc = await getDoc(webIdRef);

    if (!webIdDoc.exists()) {
      throw new Error('WebID no encontrado');
    }

    const webIdData = webIdDoc.data() as WebIDData;

    // Verificar que esté habilitado
    if (!webIdData.isEnabled) {
      throw new Error('Este perfil de emergencia no está habilitado');
    }

    // Verificar que no esté ya activado por otro usuario
    if (webIdData.userId && webIdData.userId !== userId) {
      throw new Error('Este perfil de emergencia ya está activado por otro usuario');
    }

    await updateDoc(webIdRef, {
      userId: userId,
      activationStatus: 'activated',
      activatedAt: Timestamp.now(),
      activatedBy: userId,
      status: 'assigned',
      updatedAt: Timestamp.now(),
    });

    console.log(`WebID ${webId} activado por usuario ${userId}`);
  } catch (error) {
    console.error('Error activando WebID:', error);
    throw error;
  }
};

/**
 * Completar onboarding de WebID
 */
export const completeWebIDOnboarding = async (
  webId: string,
  userId: string
): Promise<void> => {
  try {
    const webIdRef = doc(db, 'webids', webId);

    await updateDoc(webIdRef, {
      activationStatus: 'onboarding_completed',
      onboardingCompleted: true,
      onboardingCompletedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    console.log(`Onboarding completado para WebID ${webId} por usuario ${userId}`);
  } catch (error) {
    console.error('Error completando onboarding:', error);
    throw new Error('Error al completar configuración del perfil');
  }
};

/**
 * Verificar si un WebID está completamente activado
 */
export const isWebIDFullyActivated = (webIdData: WebIDData): boolean => {
  return webIdData.activationStatus === 'onboarding_completed' ||
         (webIdData.activationStatus === 'activated' && webIdData.onboardingCompleted === true);
};

/**
 * Verificar si un WebID puede ser activado
 */
export const canActivateWebID = async (webId: string): Promise<{
  canActivate: boolean;
  reason?: string;
  webIdData?: WebIDData;
}> => {
  try {
    const webIdRef = doc(db, 'webids', webId);
    const webIdDoc = await getDoc(webIdRef);

    if (!webIdDoc.exists()) {
      return { canActivate: false, reason: 'Perfil de emergencia no encontrado' };
    }

    const webIdData = webIdDoc.data() as WebIDData;

    if (!webIdData.isEnabled) {
      return { canActivate: false, reason: 'Este perfil de emergencia no está habilitado' };
    }

    // Solo considerar como "ya activado" si está completamente configurado
    if (isWebIDFullyActivated(webIdData)) {
      return { canActivate: false, reason: 'Este perfil de emergencia ya está completamente activado' };
    }

    // Si tiene userId pero no está completado, puede continuar el proceso
    // Si no tiene userId, puede ser activado por primera vez

    return { canActivate: true, webIdData };
  } catch (error) {
    console.error('Error verificando activación de WebID:', error);
    return { canActivate: false, reason: 'Error al verificar el perfil de emergencia' };
  }
};

/**
 * Obtener todos los WebIDs de un usuario
 */
export const getWebIDsByUser = async (userId: string): Promise<WebIDData[]> => {
  try {
    // ✅ Usando índice webids: userId + createdAt (configurado en Firestore)
    const q = query(
      webIdsCollection,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
      const data = doc.data() as WebIDData;
      return {
        ...data,
        // Asegurar compatibilidad con campos nuevos
        isEnabled: data.isEnabled ?? true,
        activationStatus: data.activationStatus ?? 'not_activated',
        onboardingCompleted: data.onboardingCompleted ?? false,
      };
    });

  } catch (error) {
    console.error('Error obteniendo WebIDs del usuario:', error);
    throw new Error('Error al obtener tus perfiles de emergencia');
  }
};
